/**
 * API model interfaces
 *
 * These interfaces define the structure of API requests, responses,
 * and component events for the FGA Manager web component.
 *
 * @packageDocumentation
 */

/**
 * Token response received from the host application.
 * Dispatched via 'wc-token-response' CustomEvent.
 */
export interface TokenResponse {
  /**
   * JWT token string
   */
  token: string;

  /**
   * Token expiration timestamp (Unix milliseconds)
   * If provided, the token will be cached until this time.
   */
  expiresAt?: number;

  /**
   * Token type (typically 'Bearer')
   */
  tokenType?: string;

  /**
   * Organisation ID from token claims (optional)
   * If provided, overrides component's organisation-id input
   */
  organisationId?: string;
}

/**
 * Decoded JWT token claims.
 * Contains standard and custom claims from the token.
 */
export interface TokenClaims {
  /**
   * Subject (user identifier)
   */
  sub?: string;

  /**
   * Organisation ID claim
   * Can be at various paths: 'org_id', 'organisation_id', 'org', or nested in 'https://platform/*'
   */
  org_id?: string;
  organisation_id?: string;
  org?: string;

  /**
   * Organisation display name claim.
   * Auth0 includes `org_name` when organizations are enabled. May also be
   * present under a namespaced claim such as 'https://platform/org_name'.
   */
  org_name?: string;

  /**
   * Token expiration time (Unix seconds)
   */
  exp?: number;

  /**
   * Token issued at time (Unix seconds)
   */
  iat?: number;

  /**
   * Issuer
   */
  iss?: string;

  /**
   * Audience
   */
  aud?: string | string[];

  /**
   * Custom claims can be at https://platform/* namespace
   */
  [key: string]: unknown;
}

/**
 * Represents a user-group membership relationship in FGA.
 * Tuple format: user:{userId} member group:{groupId}
 */
export interface FgaMembership {
  /**
   * Full FGA user identifier
   */
  userId: string;

  /**
   * Full FGA group identifier
   */
  groupId: string;

  /**
   * Relationship type (always 'member' for memberships)
   */
  relation: 'member';

  /**
   * Creation timestamp of the membership tuple
   */
  createdAt?: string;
}

/**
 * Standard error response format from the FGA proxy API.
 */
export interface FgaErrorResponse {
  /**
   * Error code identifier
   * @example 'UNAUTHORIZED', 'NOT_FOUND', 'VALIDATION_ERROR'
   */
  code: string;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Additional error details (optional)
   */
  details?: Record<string, unknown>;
}

// -----------------------------------------------------------------------------
// Component Event Types
// -----------------------------------------------------------------------------

/**
 * Error event payload emitted by FgaManagerComponent.
 * Dispatched as CustomEvent with type 'error'.
 */
export interface FgaErrorEvent {
  /**
   * Error code identifier
   */
  code: string;

  /**
   * Human-readable error message
   */
  message: string;
}

/**
 * Common error codes emitted by the component.
 */
export const FGA_ERROR_CODES = {
  /** Required inputs not provided */
  MISSING_CONFIG: 'MISSING_CONFIG',
  /** Token request timed out */
  TOKEN_TIMEOUT: 'TOKEN_TIMEOUT',
  /** General initialization failure */
  INIT_FAILED: 'INIT_FAILED',
  /** FGA API call failed */
  API_ERROR: 'API_ERROR',
  /** Permission denied (401) */
  UNAUTHORIZED: 'UNAUTHORIZED',
  /** Forbidden (403) - User lacks required permissions */
  FORBIDDEN: 'FORBIDDEN',
  /** Resource not found */
  NOT_FOUND: 'NOT_FOUND',
  /** Organisation ID not found in token */
  MISSING_ORG_ID: 'MISSING_ORG_ID',
  /** Rate limited (429) - Too many requests */
  RATE_LIMITED: 'RATE_LIMITED',
  /** Service unavailable (502/503/504) */
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE'
} as const;

/**
 * Source of an authorization error.
 */
export type UnauthorizedErrorSource = 'fga' | 'auth0' | 'unknown';

/**
 * Unauthorized error details for 401/403 responses.
 * Used by ErrorStateService to track authorization failures.
 */
export interface UnauthorizedError {
  /**
   * HTTP status code (401 for unauthenticated, 403 for forbidden)
   */
  status: 401 | 403;

  /**
   * Error source identifier - helps users understand which service denied access
   */
  source: UnauthorizedErrorSource;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Timestamp when error occurred (Unix milliseconds)
   */
  timestamp: number;
}

/**
 * Group selection event payload.
 * Dispatched as CustomEvent with type 'group-selected'.
 */
export interface GroupSelectedEvent {
  /**
   * Selected group identifier
   */
  groupId: string;
}

/**
 * User selection event payload.
 * Dispatched as CustomEvent with type 'user-selected'.
 */
export interface UserSelectedEvent {
  /**
   * Selected user identifier
   */
  userId: string;
}

/**
 * Data change event payload.
 * Dispatched as CustomEvent with type 'data-changed'.
 */
export interface DataChangedEvent {
  /**
   * Type of entity that changed
   */
  type: 'group' | 'user' | 'membership';

  /**
   * Action that triggered the change
   */
  action?: 'create' | 'update' | 'delete';

  /**
   * Identifier of the affected entity
   */
  entityId?: string;
}

/**
 * Loading state change event payload.
 * Dispatched as CustomEvent with type 'loading-state-changed'.
 * Allows host applications to show loading indicators or disable interactions.
 */
export interface LoadingStateChangedEvent {
  /**
   * Whether any data loading operation is in progress
   */
  loading: boolean;

  /**
   * Source of the loading state change
   */
  source?: 'groups' | 'users' | 'memberships' | 'initialization';
}

// -----------------------------------------------------------------------------
// Configuration Types
// -----------------------------------------------------------------------------

/**
 * Configuration for the FGA Manager component.
 * These values are passed as component inputs.
 */
export interface FgaManagerConfig {
  /**
   * Organisation scope for all FGA operations
   */
  organisationId: string;

  /**
   * Base URL for the FGA proxy API
   */
  apiBase: string;

  /**
   * Visual theme
   */
  theme?: 'light' | 'dark';

  /**
   * Disable write operations
   */
  readonly?: boolean;
}

// -----------------------------------------------------------------------------
// HTTP Error Types
// -----------------------------------------------------------------------------

/**
 * Generic HTTP error from API calls.
 * Extensible to cover any HTTP status code.
 * Used by services to communicate errors to components.
 *
 * 401/403 → "Unauthorised access"
 * 429 → "Too many requests"
 * 500+ → "Service unavailable"
 */
export interface ApiHttpError {
  /**
   * HTTP status code
   * @example 401, 403, 429, 500
   */
  status: number;

  /**
   * Error code from FGA_ERROR_CODES
   */
  code: string;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Timestamp when error occurred (Unix milliseconds)
   */
  timestamp?: number;

  /**
   * Additional error details
   */
  details?: Record<string, unknown>;
}

// -----------------------------------------------------------------------------
// State Types
// -----------------------------------------------------------------------------

/**
 * Group state managed by GroupStateService.
 */
export interface GroupState {
  /**
   * List of groups
   */
  groups: import('./group.model').FgaGroup[];

  /**
   * Currently selected group ID
   */
  selectedGroupId: string | null;

  /**
   * Loading indicator
   */
  loading: boolean;

  /**
   * Error message
   */
  error: string | null;
}

/**
 * User state managed by UserStateService.
 */
export interface UserState {
  /**
   * List of users
   */
  users: import('./user.model').FgaUser[];

  /**
   * Currently selected user ID
   */
  selectedUserId: string | null;

  /**
   * Loading indicator
   */
  loading: boolean;

  /**
   * Error message
   */
  error: string | null;
}
